import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';

import { BaasI18n, openUrl } from '@lvflanders/bar';
import { resolveSignalValue } from '@lvflanders/bar-ng';

import { HtmlToPlaintextPipe } from '../../assets/html-to-plaintext.pipe';
import { BaasLink } from '../../baas-link/baas-link';
import { BaasInteractiveElement } from '../../interactive-element/baas-interactive-element';
import { BaasTileOptions } from '../baas-tile-options.type';

@Component({
    selector: 'baas-tile',
    templateUrl: 'baas-tile.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [MatTooltipModule, BaasLink, BaasInteractiveElement, HtmlToPlaintextPipe]
})
export class BaasTile {
    readonly #router = inject(Router);
    readonly #i18n = inject(BaasI18n);

    readonly tile = input.required<BaasTileOptions>();

    readonly favorite = output<boolean | { id: number | string; value: boolean }>();

    #cancelClick = false;

    readonly additionalTileClasses = computed(() => {
        const classList: Record<string, unknown> = {
            'bar-tile--disabled': this.tile().disabled,
            'bar-tile--locked': this.tile().locked,
            'bar-tile--clickable': !this.tile().disabled && (this.tile().href || this.tile().routerCommands || this.tile().routerUrl || this.tile().action),
            'bar-tile--hidden': this.tile().hidden,
            'bar-tile--lateral': this.tile().style === 'lateral'
        };

        classList[`bar-tile--size-${this.#size()}`] = true;
        return classList;
    });

    protected readonly srText = computed(() => {
        return this.tile().icon.srText;
    });

    protected readonly truncateTitle = computed(() => {
        return this.locked() || (this.tile().truncateTitle == null ? true : this.tile().truncateTitle);
    });

    protected readonly tooltip = computed(() => {
        return this.tile().tooltip || '';
    });

    protected readonly contentTooltip = computed(() => {
        return this.tile().contentTooltip || (this.tile().defaultContentTooltipEnabled !== false ? this.tile().content : undefined);
    });

    protected readonly icon = computed(() => {
        return !!this.tile().icon;
    });

    protected readonly iconClass = computed(() => {
        const untouchedClass = this.tile().icon.class;
        return this.#isCustomIcon() ? `bar-tile__custom-icon ${untouchedClass}` : untouchedClass;
    });

    protected readonly iconStyle = computed(() => {
        return 'color: #8bae00; ' + this.tile().icon.style;
    });

    protected readonly bodyTooltip = computed(() => {
        return this.tile().bodyTooltip || (this.tile().defaultBodyTooltipEnabled !== false ? this.tile().body : undefined);
    });

    protected readonly truncateBody = computed(() => {
        return this.locked() || (this.tile().truncateBody == null ? true : this.tile().truncateBody);
    });

    protected readonly moreInformationLink = computed(() => {
        return this.tile().moreInformationLink;
    });

    protected readonly isFavorite = computed(() => {
        return this.tile().isFavorite;
    });

    protected readonly hideFavoriteIndicator = computed(() => {
        return this.tile().hideFavoriteIndicator;
    });

    // accessible name for the favorite toggle, since it has no visible text
    protected readonly favoriteLabel = computed(() => {
        return this.isFavorite() ? this.#i18n.get('DOMAIN_BAR_CONTROLS.TILE.CANCEL_FAVORITE') : this.#i18n.get('DOMAIN_BAR_CONTROLS.TILE.MARK_FAVORITE');
    });

    protected readonly disabled = computed(() => resolveSignalValue(this.tile().disabled));

    protected readonly locked = computed(() => resolveSignalValue(this.tile().locked));

    protected readonly hidden = computed(() => resolveSignalValue(this.tile().hidden));

    readonly #isCustomIcon = computed(() => {
        return this.tile().icon.class.includes('bar-tile__custom-icon');
    });

    readonly #size = computed(() => this.tile().size || 'normal');

    protected emitFavorite(event) {
        event.stopPropagation();
        if (this.tile().id) {
            this.favorite.emit({ id: this.tile().id, value: !this.isFavorite });
        } else {
            this.favorite.emit(!this.isFavorite);
        }
    }

    protected async tileClicked(event) {
        if (this.#cancelClick) {
            this.#cancelClick = false;
            return;
        }
        event.preventDefault();
        if (!this.tile().disabled) {
            if (this.tile().action) {
                const actionResult = this.tile().action();
                if (actionResult instanceof Promise) await actionResult;
            }

            if (this.tile().routerCommands) {
                const params = { queryParams: {}, fragment: '' };
                if (this.tile().queryParams) params.queryParams = this.tile().queryParams;
                if (this.tile().fragment) params.fragment = this.tile().fragment;
                else delete params.fragment;
                this.#router.navigate(this.tile().routerCommands, params);
            }

            if (this.tile().routerUrl) this.#router.navigateByUrl(this.tile().routerUrl);

            if (this.tile().href) {
                openUrl(this.tile().href, this.tile().target);
            }
        }
    }

    protected linkClicked() {
        this.#cancelClick = true;
    }

    protected readonly lockedLink = computed(() => this.tile().lockedLink);
}
